import { useState } from "react";

import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Center,
  Grid,
  GridItem,
  HStack,
  Icon,
  Image,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  VStack
} from "@chakra-ui/react";

import { format } from "date-fns";
import { formatDistanceToNow } from "date-fns/formatDistanceToNow";
import { BiCheckCircle } from "react-icons/bi";
import { TbBuildingBank, TbHistory, TbTrash, TbWallet } from "react-icons/tb";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { FeatureType } from "@/jenesys-types/data/enums";
import {
  BankAccount,
  IBank,
  IBankConnection
} from "@/jenesys-types/jenesys-entities/config";

import { ConfirmModal } from "@/components/common/controls/ConfirmModal";
import useActionTrigger from "@/hooks/util/useActionTrigger";
import { ConnectBank } from "@/routes/lazyFeatures";
import {
  removeBankConnection as removeBankConnectionAction,
  selectConnectionsSettings
} from "@/store/features/clientSettings/connections/connections";
import {
  deleteBankAccount as deleteBankAccountAction,
  selectReconciliation
} from "@/store/features/reconciliation/reconciliation";
import { bankReconPlatforms, toastError, toastSuccess } from "@/util/helpers";

import { CancelPermissions } from "./CancelPermissions";
import { ReAuthoriseConnection } from "./ReAuthoriseConnection";

interface Props {
  banks: Array<IBank>;
  bankConnections: Array<IBankConnection>;
  isFetchingBankConnections: boolean;
  platform: (typeof bankReconPlatforms)[0];
}

export default function ConnectedBankAccounts({
  banks,
  platform,
  bankConnections,
  isFetchingBankConnections
}: Props) {
  const [selectedConnection, setSelectedConnection] = useState<
    (IBankConnection & { bank?: IBank }) | null
  >(null);
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(
    null
  );
  const cancelPermissionsDisclosure = useDisclosure();
  const removeBankDisclosure = useDisclosure();
  const removeAccountDisclosure = useDisclosure();
  const reauthorizeDisclosure = useDisclosure();
  const newConnectionDisclosure = useDisclosure();
  const { clientId } = useParams();
  const dispatch = useDispatch();
  const { deleteBankAccountLoading } = useSelector(selectReconciliation);
  const { isRemovingBankConnection } = useSelector(selectConnectionsSettings);
  const platformConnections = bankConnections.filter(
    (connection) => connection.status === platform.status
  );

  const {
    trigger: triggerExtendPermissions,
    isLoading: isExtendingConnection
  } = useActionTrigger<{
    documentId: string;
    companyId: string;
  }>({
    actionName: "extendPermissions",
    pathParams: { connectionId: selectedConnection?.id, companyId: clientId },
    featureCluster: FeatureType.Integrations,
    onSuccess: () => {
      toastSuccess("Permissions extended successfully!");
    },
    onError: () => {
      toastError("Unable to extend permissions");
    }
  });

  const handleRemoveBankConnection = (connectionId: string) => {
    if (!clientId || !connectionId) return;

    dispatch(
      removeBankConnectionAction({
        clientId,
        connectionId,
        onSuccess: () => {
          removeBankDisclosure.onClose();
          setSelectedConnection(null);
          toastSuccess("Bank removed successfully");
        }
      })
    );
  };

  const handleRemoveBankAccount = (accountId: string) => {
    if (!clientId || !accountId) return;
    dispatch(
      deleteBankAccountAction({
        companyId: clientId,
        bankAccountId: accountId,
        onSuccess: () => {
          removeAccountDisclosure.onClose();
          setSelectedAccount(null);
          toastSuccess("Account removed successfully");
        }
      })
    );
  };

  return (
    <Box>
      <HStack justify="space-between">
        <Box>
          <Text mt={2} fontWeight={600}>
            Manage Account(s)
          </Text>
          <Text fontSize={12} mt={1}>
            View connected bank accounts for this client
          </Text>
        </Box>

        <Button onClick={newConnectionDisclosure.onOpen} size="sm" h={9}>
          Connect New Account
        </Button>
      </HStack>

      <Stack mt={8} spacing={8}>
        {platformConnections.map((connection) => {
          const bank = banks.find((b) => b.id === connection.institution_id);
          const isActive = connection.status === "ACTIVE";
          const isManual = connection.status === "MANUAL";
          const isExpired = connection.status === "EXPIRED";

          return (
            <Box
              onClick={() => setSelectedConnection({ ...connection, bank })}
              rounded={16}
              bg="#F7F5F9"
              key={connection.id}
              px={4}
              py={4}
            >
              <HStack justify="space-between">
                <HStack spacing={3}>
                  <Image
                    boxSize={7}
                    src={bank?.raw.media[0]?.source || "/images/bank.png"}
                    alt={bank?.name}
                  />

                  <Text fontSize={14} fontWeight={600}>
                    {connection.institution?.name}
                  </Text>
                </HStack>

                <Center
                  rounded="full"
                  px={2}
                  py={1.5}
                  bg={isActive || isManual ? "green.100" : "red.100"}
                  color={isActive || isManual ? "green.500" : "red.500"}
                >
                  <BiCheckCircle />
                  <Text fontSize={12} fontWeight={600} ml={1}>
                    {connection.status}
                  </Text>
                </Center>
              </HStack>

              <Accordion mt={4} defaultIndex={[0]} allowMultiple>
                <AccordionItem border={0}>
                  <h2>
                    <AccordionButton px={2}>
                      <HStack as="span" flex="1" textAlign="left">
                        <Center boxSize={7} rounded={3} bg="blackAlpha.50">
                          <Icon
                            fontSize={20}
                            as={TbBuildingBank}
                            color="secondary.500"
                          />
                        </Center>
                        <Text fontSize={14} fontWeight={500}>
                          Your Bank Account(s) details
                        </Text>
                      </HStack>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel px={0} pb={4}>
                    <Stack spacing={2}>
                      {connection.accounts.map((account) => (
                        <Grid
                          key={account.id}
                          rounded={8}
                          bg="white"
                          py={3}
                          px={4}
                          templateColumns="repeat(24, 1fr)"
                          rowGap={4}
                          columnGap={3}
                        >
                          <GridItem colSpan={{ base: 24, xl: 4 }}>
                            <Text fontSize={14} fontWeight={700}>
                              {account.name}
                            </Text>
                          </GridItem>

                          <GridItem
                            colSpan={{ base: 24, sm: 12, lg: 8, xl: 6 }}
                          >
                            <Text fontSize={12} fontWeight={500}>
                              Account Number
                            </Text>
                            <Text fontSize={12} fontWeight={600}>
                              {account.account_number || account.account_id}
                            </Text>
                          </GridItem>

                          {isActive && !isManual && (
                            <>
                              <GridItem
                                colSpan={{ base: 24, sm: 12, lg: 8, xl: 4 }}
                              >
                                <Text fontSize={12} fontWeight={500}>
                                  Access Granted
                                </Text>
                                <Text fontSize={12} fontWeight={600}>
                                  {isManual
                                    ? "N/A"
                                    : format(
                                        connection.created_at,
                                        "dd/MM/yyyy"
                                      )}
                                </Text>
                              </GridItem>
                              <GridItem
                                colSpan={{ base: 24, sm: 12, lg: 8, xl: 4 }}
                              >
                                <Text fontSize={12} fontWeight={500}>
                                  Last Access Refresh
                                </Text>
                                <Text fontSize={12} fontWeight={600}>
                                  {isManual
                                    ? "N/A"
                                    : format(
                                        connection.last_confirmed_at,
                                        "dd/MM/yyyy"
                                      )}
                                </Text>
                              </GridItem>
                              <GridItem
                                colSpan={{ base: 24, sm: 12, lg: 8, xl: 4 }}
                              >
                                <Text fontSize={12} fontWeight={500}>
                                  Access Expires
                                </Text>
                                <Text fontSize={12} fontWeight={600}>
                                  &nbsp;
                                  {isManual
                                    ? "N/A"
                                    : formatDistanceToNow(
                                        connection.reconfirm_by,
                                        {
                                          addSuffix: true
                                        }
                                      )}
                                </Text>
                              </GridItem>
                            </>
                          )}
                          {isManual && (
                            <>
                              <GridItem
                                colSpan={{ base: 24, sm: 12, lg: 8, xl: 4 }}
                              >
                                <Text fontSize={12} fontWeight={500}>
                                  Account Type
                                </Text>
                                <Text
                                  textTransform="capitalize"
                                  fontSize={12}
                                  fontWeight={600}
                                >
                                  {account.account_type}
                                </Text>
                              </GridItem>
                              <GridItem
                                colSpan={{ base: 24, sm: 12, lg: 8, xl: 4 }}
                              >
                                <Text fontSize={12} fontWeight={500}>
                                  Date Added
                                </Text>
                                <Text fontSize={12} fontWeight={600}>
                                  {format(connection.created_at, "dd/MM/yyyy")}
                                </Text>
                              </GridItem>
                              <GridItem
                                colSpan={{ base: 24, sm: 12, lg: 8, xl: 4 }}
                              >
                                <Button
                                  onClick={() => {
                                    setSelectedAccount(account);
                                    removeAccountDisclosure.onOpen();
                                  }}
                                  variant="default"
                                  bg="white"
                                  w="full"
                                  leftIcon={<Icon as={TbTrash} />}
                                  color="red.600"
                                  size="sm"
                                  h={9}
                                >
                                  Remove
                                </Button>
                              </GridItem>
                            </>
                          )}
                        </Grid>
                      ))}
                    </Stack>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem border={0}>
                  <h2>
                    <AccordionButton px={2}>
                      <HStack as="span" flex="1" textAlign="left">
                        <Center boxSize={7} rounded={3} bg="blackAlpha.50">
                          <Icon
                            fontSize={20}
                            as={TbWallet}
                            color="secondary.500"
                          />
                        </Center>
                        <Text fontSize={14} fontWeight={500}>
                          Your Account(s) Balance Information
                        </Text>
                      </HStack>
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4} />
                </AccordionItem>
              </Accordion>

              <HStack ml={2} as="span" flex="1" textAlign="left">
                <Center boxSize={7} rounded={3} bg="blackAlpha.50">
                  <Icon fontSize={20} as={TbHistory} color="secondary.500" />
                </Center>
                <Text fontSize={14} fontWeight={500}>
                  Your Transaction history
                </Text>
              </HStack>

              <Box mt={4}>
                {!isManual && (
                  <Button
                    onClick={() =>
                      triggerExtendPermissions({
                        pathParams: {
                          connectionId: connection.id,
                          companyId: clientId
                        }
                      })
                    }
                    isLoading={
                      isExtendingConnection &&
                      connection.id === selectedConnection?.id
                    }
                    loadingText="Extending..."
                    variant="default"
                    border="1px solid"
                    borderColor="gray.200"
                    bg="white"
                    w="full"
                    size="sm"
                    h={9}
                  >
                    Extend Permissions
                  </Button>
                )}

                {isActive && !isManual && (
                  <Button
                    onClick={cancelPermissionsDisclosure.onOpen}
                    mt={4}
                    variant="default"
                    border="1px solid"
                    borderColor="gray.200"
                    bg="white"
                    w="full"
                    size="sm"
                    h={9}
                  >
                    Cancel Permissions
                  </Button>
                )}

                {isManual && (
                  <Button
                    onClick={removeBankDisclosure.onOpen}
                    mt={4}
                    variant="default"
                    border="1px solid"
                    borderColor="gray.200"
                    bg="white"
                    w="full"
                    size="sm"
                    h={9}
                  >
                    Remove Bank
                  </Button>
                )}

                {isExpired && (
                  <Button
                    onClick={reauthorizeDisclosure.onOpen}
                    mt={4}
                    variant="default"
                    border="1px solid"
                    borderColor="gray.200"
                    bg="white"
                    w="full"
                    size="sm"
                    h={9}
                  >
                    Re-authorise
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>

      {removeBankDisclosure.isOpen && selectedConnection && (
        <ConfirmModal
          title="Confirm Removal"
          content={`Are you sure you want to remove ${selectedConnection.institution?.name} from your connections? This action remove all accounts associated with the bank.`}
          isLoading={isRemovingBankConnection}
          loadingText="Removing..."
          confirmAction={() =>
            handleRemoveBankConnection(selectedConnection.id)
          }
          confirmText="Yes, Remove"
          {...removeBankDisclosure}
        />
      )}

      {removeAccountDisclosure.isOpen &&
        selectedConnection &&
        selectedAccount && (
          <ConfirmModal
            title="Confirm Removal"
            content={`Are you sure you want to remove "${selectedAccount?.name}" from ${selectedConnection.institution?.name}?`}
            isLoading={deleteBankAccountLoading}
            loadingText="Removing..."
            confirmAction={() => handleRemoveBankAccount(selectedAccount.id)}
            confirmText="Yes, Remove"
            {...removeAccountDisclosure}
          />
        )}

      {cancelPermissionsDisclosure.isOpen && selectedConnection && (
        <CancelPermissions
          connection={selectedConnection}
          {...cancelPermissionsDisclosure}
        />
      )}

      {reauthorizeDisclosure.isOpen && selectedConnection && (
        <ReAuthoriseConnection
          connection={selectedConnection}
          {...reauthorizeDisclosure}
        />
      )}

      {newConnectionDisclosure.isOpen && (
        <ConnectBank {...newConnectionDisclosure} />
      )}

      {isFetchingBankConnections && (
        <Center>
          <Spinner color="primary.500" />
        </Center>
      )}

      {!isFetchingBankConnections && !platformConnections.length && (
        <VStack color="secondary.600" mt={8} spacing={4}>
          <Image objectFit="cover" alt="bank" src="/images/bank.png" />
          <Text fontSize={14} fontWeight={500}>
            No connected bank accounts
          </Text>
        </VStack>
      )}
    </Box>
  );
}
